import { useCallback, useEffect, useState } from "react";
import { fmtError, fmtEUR, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { Loader2, Wallet, Gift, ArrowDownLeft, ArrowUpRight } from "lucide-react";

export default function WalletPage() {
  const { api } = useTenant();
  const [wallet, setWallet] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!api) return;
    api.myWallet().then(setWallet).catch((e) => setError(fmtError(e)));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    setError("");
    setMsg(null);
    try {
      const r = await api.redeem(code.trim());
      setMsg(`Gift card riscattata! +${fmtEUR(r.amount)} sul wallet.`);
      setCode("");
      await load();
    } catch (err) {
      setError(fmtError(err));
    } finally {
      setRedeeming(false);
    }
  };

  if (!wallet) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-[#A78BFA]" /></div>;
  }

  return (
    <div data-testid="wallet-page" className="max-w-4xl">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#A78BFA] mb-3">Wallet</div>
      <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">
        Il tuo <span className="italic">portafoglio</span> AURA.
      </h1>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        {/* Balance */}
        <div className="relative rounded-3xl border border-black/5 p-10 overflow-hidden bg-gradient-to-br from-[#F5F0FF] via-white to-[#ECFEFF] tilt-card" data-testid="wallet-balance-card">
          <div aria-hidden className="pointer-events-none absolute -top-12 -right-12 h-44 w-44 rounded-full blur-3xl opacity-60 animate-blob"
            style={{ background: "radial-gradient(circle, #C4B5FD 0%, transparent 70%)" }} />
          <div className="relative flex items-start justify-between">
            <div className="h-11 w-11 rounded-2xl bg-white shadow-sm border border-black/5 flex items-center justify-center">
              <Wallet size={20} className="text-[#7C3AED]" />
            </div>
          </div>
          <div className="relative mt-8">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#737373]">Saldo disponibile</div>
            <div className="font-serif font-light text-6xl text-[#1A1A1A] mt-1" data-testid="wallet-balance">
              {fmtEUR(wallet.balance)}
            </div>
            <div className="text-xs text-[#737373] mt-2">Spendi durante i tuoi trattamenti</div>
          </div>
        </div>

        {/* Redeem */}
        <form onSubmit={redeem} className="rounded-3xl border border-black/5 p-10 bg-white" data-testid="wallet-redeem-form">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#A78BFA] to-[#67E8F9] text-white flex items-center justify-center">
              <Gift size={20} />
            </div>
            <div>
              <h3 className="font-serif text-2xl text-[#1A1A1A]">Riscatta una gift card</h3>
              <p className="text-sm text-[#4A4A4A] font-light mt-1">Inserisci il codice ricevuto.</p>
            </div>
          </div>
          <div className="mt-6">
            <input
              data-testid="wallet-redeem-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="AURA-XXXXXXXX"
              className="w-full bg-[#FAFAFA] border border-black/10 rounded-2xl px-4 py-3.5 text-sm tracking-wider focus:outline-none focus:border-[#A78BFA]"
            />
            {msg && <div data-testid="wallet-redeem-success" className="mt-3 text-sm text-[#065F46] bg-[#D1FAE5] border border-[#A7F3D0] rounded-2xl px-4 py-2.5">{msg}</div>}
            {error && <div data-testid="wallet-redeem-error" className="mt-3 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2.5">{error}</div>}
            <button
              type="submit"
              data-testid="wallet-redeem-submit"
              disabled={redeeming || !code.trim()}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-50 text-white rounded-full px-6 py-3 text-sm tracking-wide"
            >
              {redeeming && <Loader2 size={16} className="animate-spin" />} Riscatta
            </button>
          </div>
        </form>
      </div>

      <div className="mt-12">
        <h2 className="font-serif text-3xl text-[#1A1A1A]">Movimenti</h2>
        <div className="mt-5 rounded-3xl border border-black/5 bg-white overflow-hidden" data-testid="wallet-transactions">
          {wallet.transactions.length === 0 ? (
            <div className="p-8 text-center text-[#737373] font-light">Nessun movimento ancora.</div>
          ) : (
            <ul className="divide-y divide-black/5">
              {wallet.transactions.map((t, i) => (
                <li key={t.id} data-testid={`tx-${i}`} className="px-6 py-4 flex items-center gap-4">
                  <div className={`h-10 w-10 rounded-2xl flex items-center justify-center ${
                    t.amount >= 0 ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#B91C1C]"
                  }`}>
                    {t.amount >= 0 ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1A1A1A]">{t.note || t.kind}</div>
                    <div className="text-xs text-[#737373]">{fmtDate(t.created_at)}</div>
                  </div>
                  <div className={`font-medium ${t.amount >= 0 ? "text-[#065F46]" : "text-[#B91C1C]"}`}>
                    {t.amount >= 0 ? "+" : ""}{fmtEUR(t.amount)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
